import { createFileRoute } from "@tanstack/react-router";
import { assertIsNotNull, assertIsNotUndefined, ensureDefined, ensureEnumValue } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { aabb2Move, aabb2New } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { ImageDiff } from "gfx/imageBuffer/diffPlanImages";
import { diffPlanImages } from "gfx/imageBuffer/diffPlanImages";
import type { AbstractImageData } from "gfx/imageBuffer/imageBuffer";
import { abstractImageDataToImageData } from "gfx/imageBuffer/imageBuffer";
import type { ChangeEvent } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "ui/buttons/Button";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import styles from "./pdf-diff.module.scss";

interface PDFPageReference {
  readonly id: string;
  readonly page: number;
  readonly pageAABB: AABB2; // relative [0 -> 1, 0 -> 1] bottom left 0 0
}

interface PDFViewSelection {
  readonly pageIndex: number;
  readonly pageSelection: AABB2;
  readonly pageSelectionData: string;
}

function PDFDiff() {
  return (
    <div className={styles.wrapper}>
      {/* This component won't render on the server due to the dom requirement */}
      {typeof window !== "undefined" && <PDFDiffView />}
    </div>
  );
}

function PDFDiffView() {
  // external pdfLib script - this is a temporary hack
  const [pdfLib, setPdfLib] = useState<any>();
  useEffect(() => loadPDFLib(setPdfLib), []);

  // canvas for render comparison
  const AB = useMemo(() => {
    return document.createElement("canvas");
  }, []);

  // const [snapshots, setSnapshots] = useState<string[]>([]); // not in use currently

  const [dataA, setDataA] = useState<AbstractImageData>();
  const [dataB, setDataB] = useState<AbstractImageData>();
  const [dataDiff, setDataDiff] = useState<{ diff: string; a: string; b: string; diffResult: ImageDiff }>();

  const [pdfSelections] = useState([]); // selection aabbs - not in use currently
  const [pdfPagePosition, setPDFPagePosition] = useState<AABB2>();

  if (!pdfLib) {
    return null;
  }

  // const diffSnapshots
  async function diffAB() {
    if (AB && dataA && dataB) {
      if (dataA.width !== dataB.width || dataA.height !== dataB.height) {
        alert("cannot diff PDFs, page sizes differ");
        return;
      }

      console.warn("running diff");
      const p0 = performance.now();

      const A = dataA;
      const B = dataB;

      const diffResult = await diffPlanImages(A, B, { snapshot: false });
      const diffImageData = abstractImageDataToImageData(diffResult.diff);

      AB.width = diffImageData.width;
      AB.height = diffImageData.height;

      const ctx = AB.getContext("2d");
      assertIsNotNull(ctx);
      ctx.putImageData(diffImageData, 0, 0);

      const p1 = performance.now();
      console.warn(`diff complete in ${(p1 - p0) / 1000}ms`);

      const diff = AB.toDataURL();

      ctx.putImageData(abstractImageDataToImageData(diffResult.diffA), 0, 0);
      const a = AB.toDataURL();

      ctx.putImageData(abstractImageDataToImageData(diffResult.diffB), 0, 0);
      const b = AB.toDataURL();

      setDataDiff({ diff, a, b, diffResult });

      // const snData = diffResult.snapshots.map((s) => {
      //   AB.width = s.data.width;
      //   AB.height = s.data.height;
      //   ctx.putImageData(abstractImageDataToImageData(s.data), 0, 0);
      //   return AB.toDataURL();
      // });
      // setSnapshots(snData);
    }
  }

  const onPdfPageMove = (pos: AABB2) => {
    setPDFPagePosition(pos);
  };
  const onPdfFileLoaded = (file: File) => {
    console.log(file);
  };
  const onPdfPageAABBSelected = (selection: PDFViewSelection) => {
    console.log(selection);
  };
  const onPdfSelectionCleared = () => {
    console.log("selection cleared");
  };

  return (
    <>
      <div style={{ display: "flex" }}>
        <div className={styles.wrapper} style={{ flex: 1 }}>
          <PDFRenderer
            pdfLib={pdfLib}
            onPdfFileLoaded={onPdfFileLoaded}
            onPdfPageRendered={(p) => {
              setDataA(p);
              setDataDiff(undefined);
            }}
            onPdfPageAABBSelected={onPdfPageAABBSelected}
            onPdfSelectionCleared={onPdfSelectionCleared}
            onPdfPageMove={onPdfPageMove}
            pdfSelections={pdfSelections}
            pdfPagePosition={pdfPagePosition}
            pdfDiff={dataDiff?.b}
            pdfDiffBoth={dataDiff?.diff}
          />
        </div>
        <div className={styles.wrapper} style={{ flex: 1 }}>
          <PDFRenderer
            pdfLib={pdfLib}
            onPdfFileLoaded={onPdfFileLoaded}
            onPdfPageRendered={(p) => {
              setDataB(p);
              setDataDiff(undefined);
            }}
            onPdfPageAABBSelected={onPdfPageAABBSelected}
            onPdfSelectionCleared={onPdfSelectionCleared}
            onPdfPageMove={onPdfPageMove}
            pdfSelections={pdfSelections}
            pdfPagePosition={pdfPagePosition}
            pdfDiff={dataDiff?.a}
            pdfDiffBoth={dataDiff?.diff}
          />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-evenly" }}>
        <PDFDiffControls onDiffClick={diffAB} disabled={!!dataDiff || !dataA || !dataB} />
      </div>
      {/* <SnapShots data={snapshots} /> */}
    </>
  );
}

function PDFDiffControls({ onDiffClick, disabled }: { onDiffClick?: () => void; disabled?: boolean }) {
  return (
    <>
      <Button disabled={disabled} btnSize="jumbo" btnStyle="remove" onClick={onDiffClick}>
        Run Diff
      </Button>
    </>
  );
}

// function SnapShots({ data }: { data: string[] }) {
//   return (
//     <div className={styles.wrapper}>
//       <div className={styles.snapshots}>
//         {data.map((d, i) => {
//           return <img className={styles.snapshot} key={i} src={d} />;
//         })}
//       </div>
//     </div>
//   );
// }

enum PDFRendererPageViewMode {
  PAGE = "PAGE",
  DIFF = "DIFF",
  BOTH = "BOTH",
}

interface PDFRendererProps {
  readonly pdfLib: any;
  readonly pdfSelections: PDFPageReference[];
  readonly pdfDiff?: string;
  readonly pdfDiffBoth?: string;
  readonly pdfPagePosition?: AABB2;
  readonly onPdfFileLoaded: (pdfFile: File) => void;
  readonly onPdfPageAABBSelected: (pdfSel: PDFViewSelection) => void;
  readonly onPdfPageRendered: (pageData: AbstractImageData) => void;
  readonly onPdfSelectionCleared: () => void;
  readonly onPdfPageMove: (pos: AABB2) => void;
  readonly enableSelection?: boolean;
}

function PDFRenderer({
  pdfLib,
  pdfSelections,
  pdfDiff,
  pdfDiffBoth,
  pdfPagePosition,
  onPdfFileLoaded,
  onPdfPageRendered,
  onPdfPageAABBSelected,
  onPdfPageMove,
}: PDFRendererProps) {
  // pdf file for rendering
  const [pdfFile, setPdfFile] = useState<File>();
  const [pdfState, setPdfState] = useState<PDFState>();
  const [pdfPageData, setPdfPageData] = useState<string>();
  const [pdfPageViewMode, setPdfRendererPageViewMode] = useState(PDFRendererPageViewMode.DIFF);

  // image to display
  const pdfPageSrc = useMemo(() => {
    switch (pdfPageViewMode) {
      case PDFRendererPageViewMode.BOTH:
        return pdfDiffBoth ?? pdfPageData;
      case PDFRendererPageViewMode.DIFF:
        return pdfDiff ?? pdfPageData;
      case PDFRendererPageViewMode.PAGE:
        return pdfPageData;
    }
  }, [pdfPageViewMode, pdfPageData, pdfDiff, pdfDiffBoth]);

  // renderer
  const pdfRenderer = useMemo(() => {
    const renderer = new PDFPageCanvasRenderer(pdfLib);
    renderer.on("stateChange", setPdfState); // hook up the renderer to React
    return renderer;
  }, [pdfLib]);

  // cleanup
  useEffect(() => {
    return () => {
      if (pdfRenderer) {
        pdfRenderer.destroy();
      }
    };
  }, []);

  // load pdf files into renderer
  useEffect(() => {
    const loadPdfFile = async () => {
      if (!pdfRenderer || !pdfFile) {
        return;
      }
      await pdfRenderer.loadPDF(pdfFile);
      onPdfFileLoaded(pdfFile);
    };

    void loadPdfFile();
  }, [pdfRenderer, pdfFile]);

  // render page data
  useEffect(() => {
    const renderPdfPage = async () => {
      if (!pdfRenderer?.state?.pdf) {
        return;
      }
      const pdfPageIndex = pdfRenderer.getPageIndex();
      const pdfPageAABBSelections = pdfSelections.filter((s) => s.page === pdfPageIndex).map((s) => s.pageAABB);
      await pdfRenderer.render();
      onPdfPageRendered(pdfRenderer.getData()); // we call this before adding the aabb selections
      await pdfRenderer.renderAABBs(pdfPageAABBSelections, "#d63c3c");
      setPdfPageData(pdfRenderer.getDataURL());
    };

    void renderPdfPage();
  }, [pdfState, pdfSelections]);

  // canvas for aabb selection
  // this should probably be disposable or be in the renderer class
  const pdfSelectionCanvas = useMemo(() => {
    return document.createElement("canvas");
  }, []);

  const onAABBSelect = async (pageSelection: AABB2) => {
    if (!pdfRenderer || !pdfSelectionCanvas) {
      return;
    }

    // we need a second render pass here to get the canvas without the overlay aabbs.
    await pdfRenderer.render();

    const pageIndex = pdfRenderer.getPageIndex();
    const pageData = pdfRenderer.getAABBData(pageSelection);

    pdfSelectionCanvas.width = pageData.width;
    pdfSelectionCanvas.height = pageData.height;

    const ctx = pdfSelectionCanvas.getContext("2d");
    assertIsNotNull(ctx);
    ctx.putImageData(pageData, 0, 0);

    const pageSelectionData = pdfSelectionCanvas.toDataURL();

    onPdfPageAABBSelected({
      pageIndex,
      pageSelection,
      pageSelectionData,
    });
  };

  return (
    <>
      <PDFRendererControls
        disabled={!pdfRenderer}
        pageCount={pdfRenderer?.getPageCount()}
        pageIndex={pdfRenderer?.getPageIndex()}
        pageViewMode={pdfPageViewMode}
        onPageViewModeChanged={setPdfRendererPageViewMode}
        onFileChange={setPdfFile}
        onPageChange={(n) => {
          void pdfRenderer.setPageIndex(n);
        }}
      />
      {pdfFile &&
        (true ? (
          <ImageAABBSelect
            src={ensureDefined(pdfPageSrc)}
            onAABBSelect={onAABBSelect}
            onAABBMove={onPdfPageMove}
            enableSelection={false}
            scrollToZoom
          />
        ) : (
          <ImageAABBFollow src={ensureDefined(pdfPageSrc)} aabb={ensureDefined(pdfPagePosition)} />
        ))}
    </>
  );
}

function PDFRendererControls({
  pageCount,
  pageIndex,
  pageViewMode,
  onPageChange,
  onFileChange,
  onPageViewModeChanged,
  disabled,
}: {
  pageCount: number;
  pageIndex: number;
  pageViewMode: PDFRendererPageViewMode;
  onFileChange: (file: File) => void;
  onPageChange: (pageIndex: number) => void;
  onPageViewModeChanged: (mode: PDFRendererPageViewMode) => void;
  disabled?: boolean;
}) {
  const setPageIndex = (pageIndex: number) => {
    onPageChange(pageIndex);
  };

  const onPageChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setPageIndex(+e.target.value);
  };

  const prevPage = () => {
    setPageIndex(pageIndex - 1);
  };

  const nextPage = () => {
    setPageIndex(pageIndex + 1);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: 10,
        }}
      >
        <Input
          name="url"
          type="file"
          accept=".pdf, .PDF"
          onChange={(e) => {
            assertIsNotNull(e.target.files);
            if (e.target.files.length) {
              onFileChange(e.target.files[0]);
            }
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: 10,
        }}
      >
        <Button btnSize="small" onClick={prevPage}>
          Previous Page
        </Button>
        <Input
          style={{ marginLeft: 10, marginRight: 10 }}
          disabled={disabled}
          name="page"
          type="number"
          min={1}
          max={pageCount}
          value={pageIndex}
          onChange={onPageChanged}
          title="Page Index"
        />
        <Button btnSize="small" onClick={nextPage}>
          Next Page
        </Button>
      </div>
      <div
        style={{
          display: "flex",
          paddingBottom: 10,
        }}
      >
        <Select
          value={pageViewMode}
          options={Object.values(PDFRendererPageViewMode).map((m) => ({ label: m, value: m }))}
          onChange={(value) => onPageViewModeChanged(ensureEnumValue(value, PDFRendererPageViewMode))}
        />
      </div>
    </div>
  );
}

interface MouseState {
  readonly mouseDown?: [number, number];
  readonly mouseUp?: [number, number];
  readonly mousePos?: [number, number];
}

interface KeyState {
  readonly altDown?: boolean;
}

interface ImageState {
  readonly imageScale: number;
  readonly imageX: number;
  readonly imageY: number;
}

interface AABBElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ImageSelectionState {
  imageState: ImageState;
  mouseState: MouseState;
  keyState: KeyState;
}

function defaultImageState(): ImageState {
  return { imageScale: 1, imageX: 0, imageY: 0 };
}

function defaultSelectionState(): ImageSelectionState {
  return {
    imageState: defaultImageState(),
    mouseState: {},
    keyState: {},
  };
}

function ImageAABBSelect({
  src,
  onAABBSelect,
  onAABBMove,
  enableSelection = false,
  scrollToZoom = false,
}: {
  src: string;
  onAABBSelect?: (aabb: AABB2) => void;
  onAABBMove?: (aabb: AABB2) => void;
  enableSelection?: boolean;
  scrollToZoom?: boolean;
}) {
  const overlayRef = useRef<HTMLImageElement | null>(null);

  const [selectionState, setSelectionState] = useState<ImageSelectionState>(defaultSelectionState());
  const [imageDragStart, setImageDragStart] = useState<[number, number]>([0, 0]);

  const setMouseState = (mouseState: MouseState) => {
    setSelectionState({ ...selectionState, mouseState });
  };

  const setImageState = (imageState: ImageState) => {
    setSelectionState({ ...selectionState, imageState });
  };

  // const setKeyState = (keyState: KeyState) => {
  //   setSelectionState({ ...selectionState, keyState });
  // }

  // clear the overlay if the image is changed
  // useEffect(() => {
  //   setSelectionState(defaultSelectionState());
  // }, [src]);

  // clear the mouse if the selection state changed
  useEffect(() => {
    setMouseState({});
  }, [enableSelection]);

  const imageAABB = useMemo(() => {
    const { imageX, imageY, imageScale } = selectionState.imageState;

    const offset = imageScale - 1;
    const aabb: AABB2 = {
      minX: 0 - offset,
      minY: 0 - offset,
      maxX: 1 + offset,
      maxY: 1 + offset,
    };

    aabb2Move(aabb, imageX, imageY);

    if (onAABBMove) {
      onAABBMove(aabb);
    }

    return aabb;
  }, [selectionState.imageState]);

  const selectionAABB = useMemo(() => {
    const { altDown } = selectionState.keyState;
    const { mouseDown, mouseUp, mousePos } = selectionState.mouseState;

    if (!mouseDown || !altDown) {
      return undefined;
    }

    if (!mouseUp && !mousePos) {
      return undefined;
    }

    const mStart = mouseDown;
    const mEnd = mouseUp ?? mousePos ?? [0, 0];

    const [ux, uy] = mStart;
    const [dx, dy] = mEnd;

    const minX = Math.min(ux, dx);
    const minY = Math.min(uy, dy);

    const maxX = Math.max(ux, dx);
    const maxY = Math.max(uy, dy);

    const aabb: AABB2 = {
      minX,
      minY,
      maxX,
      maxY,
    };

    return aabb;
  }, [selectionState.mouseState, selectionState.keyState]);

  const getMousePosition = (e: React.MouseEvent): [number, number] => {
    if (!overlayRef.current) {
      return [0, 0];
    }

    const { clientHeight, clientWidth } = overlayRef.current;
    const { clientX, clientY } = e;

    const clientBoundingRect = overlayRef.current.getBoundingClientRect();

    const x = clientX - clientBoundingRect.left; // x position within the element.
    const y = clientY - clientBoundingRect.top; // y position within the element.

    const dx = x / clientWidth;
    const dy = 1 - y / clientHeight;

    return [dx, dy];
  };

  const getAABBPosition = (aabb: AABB2): AABBElementPosition => {
    if (!overlayRef.current) {
      return { top: 0, left: 0, width: 0, height: 0 };
    }

    const { clientWidth, clientHeight } = overlayRef.current;
    const { minX, minY, maxX, maxY } = aabb;

    const top = (1 - maxY) * clientHeight;
    const left = minX * clientWidth;
    const width = (maxX - minX) * clientWidth;
    const height = (maxY - minY) * clientHeight;

    return {
      top,
      left,
      width,
      height,
    };
  };

  const getSelectedAABB = () => {
    if (!selectionAABB) {
      return aabb2New(0, 0, 0, 0);
    }

    const { minX, minY, maxX, maxY } = imageAABB;

    const sMinX = selectionAABB.minX;
    const sMinY = selectionAABB.minY;
    const sMaxX = selectionAABB.maxX;
    const sMaxY = selectionAABB.maxY;

    const cMinX = remapValue(sMinX, minX, maxX, 0, 1);
    const cMaxX = remapValue(sMaxX, minX, maxX, 0, 1);
    const cMinY = remapValue(sMinY, minY, maxY, 0, 1);
    const cMaxY = remapValue(sMaxY, minY, maxY, 0, 1);

    const aabb = aabb2New(cMinX, cMinY, cMaxX, cMaxY);

    return aabb;
  };

  useMemo(() => {
    if (!enableSelection) {
      return;
    }

    if (!selectionState.mouseState.mouseUp) {
      return;
    }

    assertIsNotUndefined(onAABBSelect);
    onAABBSelect(getSelectedAABB());
  }, [selectionAABB]);

  // controls

  const onMouseDown = (e: React.MouseEvent) => {
    const { imageX, imageY } = selectionState.imageState;
    const { altDown } = selectionState.keyState;

    if (!altDown) {
      setImageDragStart([imageX, imageY]);
    } else {
      setImageDragStart([0, 0]);
    }

    setMouseState({
      mouseDown: getMousePosition(e),
    });
  };

  const onMouseUp = (e: React.MouseEvent) => {
    setMouseState({
      // ...selectionState.mouseState,
      mouseUp: getMousePosition(e),
    });
  };

  const onMouseMove = (e: React.MouseEvent) => {
    const { mouseDown, mouseUp } = selectionState.mouseState;
    const { altDown } = selectionState.keyState;
    const mousePos = getMousePosition(e);

    if (!altDown && mouseDown) {
      const [sx, sy] = mouseDown;
      const [ex, ey] = mousePos;
      const dx = ex - sx;
      const dy = ey - sy;

      const imageX = imageDragStart[0] + dx;
      const imageY = imageDragStart[1] + dy;

      setImageState({
        ...selectionState.imageState,
        imageX,
        imageY,
      });

      return;
    }

    if (mouseDown && mouseUp) {
      return;
    }

    setMouseState({
      mouseDown,
      mousePos,
    });
  };

  const onMouseWheel = (e: React.WheelEvent) => {
    if (!scrollToZoom) {
      return;
    }
    e.stopPropagation();

    let { imageScale, imageX, imageY } = selectionState.imageState;
    const { deltaY } = e;
    const [mouseX, mouseY] = getMousePosition(e);

    // mouse pos relative to center
    const dx = mouseX - 0.5;
    const dy = mouseY - 0.5;

    if (deltaY < 0) {
      imageScale *= 1.1;
      imageX -= dx * imageScale * 0.25;
      imageY -= dy * imageScale * 0.25;
    } else {
      imageScale *= 0.9;
      imageX *= 0.9;
      imageY *= 0.9;
    }

    setImageState({
      imageScale,
      imageX,
      imageY,
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.altKey) {
      setSelectionState({
        ...selectionState,
        mouseState: {},
        keyState: { altDown: true },
      });
    }
  };

  const onKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" || e.keyCode === 27) {
      setImageState(defaultImageState());
      return;
    }

    if (!e.altKey) {
      setSelectionState({
        ...selectionState,
        mouseState: {},
        keyState: { altDown: false },
      });
    }
  };

  const selectionBox = useMemo(() => {
    if (!overlayRef.current || !selectionAABB) {
      return undefined;
    }

    if (!enableSelection) {
      return undefined;
    }

    return <div className={`${styles.planBox} ${styles.selection}`} style={getAABBPosition(selectionAABB)} />;
  }, [selectionAABB]);

  const imageBox = useMemo(() => {
    if (!overlayRef.current || !imageAABB) {
      return undefined;
    }

    return <img className={styles.planBox} style={getAABBPosition(imageAABB)} src={src} />;
  }, [imageAABB, src]);

  const cursor = () => {
    let cursor: string | undefined = undefined;
    if (!selectionState.keyState.altDown) {
      if (selectionState.mouseState.mouseDown) {
        cursor = "grabbing";
      } else {
        cursor = "grab";
      }
    } else if (enableSelection) {
      cursor = "crosshair";
    }
    return cursor;
  };

  return (
    <div
      className={styles.plan}
      tabIndex={0}
      style={{ cursor: cursor() }}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseMove={onMouseMove}
      onWheel={onMouseWheel}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
    >
      <img ref={overlayRef} className={styles.planImageRef} src={src} />
      <div className={styles.planOverlay}>
        {imageBox}
        {selectionBox}
      </div>
    </div>
  );
}

function ImageAABBFollow({ src, aabb }: { src: string; aabb: AABB2 }) {
  const overlayRef = useRef<HTMLImageElement | null>(null);

  const getAABBPosition = (aabb: AABB2): AABBElementPosition => {
    if (!overlayRef.current) {
      return { top: 0, left: 0, width: 0, height: 0 };
    }

    const { clientWidth, clientHeight } = overlayRef.current;
    const { minX, minY, maxX, maxY } = aabb;

    const top = (1 - maxY) * clientHeight;
    const left = minX * clientWidth;
    const width = (maxX - minX) * clientWidth;
    const height = (maxY - minY) * clientHeight;

    return {
      top,
      left,
      width,
      height,
    };
  };

  const imageBox = useMemo(() => {
    if (!overlayRef.current || !aabb) {
      return undefined;
    }

    return <img className={styles.planBox} style={getAABBPosition(aabb)} src={src} />;
  }, [aabb, src]);

  return (
    <div className={styles.plan}>
      <img ref={overlayRef} className={styles.planImageRef} src={src} />
      <div className={styles.planOverlay}>{imageBox}</div>
    </div>
  );
}

// ##################################################
// below this should be moved into /lib eventually
// ##################################################

interface PDFState {
  pdf: any;
  pdfPage: any;
  pdfPageIndex: number;
  pdfImg?: any;
}

type PDFRendererEvent = "stateChange" | "render";
type PDFRendererEventSubscribers = {
  [K in PDFRendererEvent]: {
    [id: string]: (state: PDFState) => void;
  };
};

class PDFPageCanvasRenderer {
  private context: CanvasRenderingContext2D;
  private subscribers: PDFRendererEventSubscribers = {
    stateChange: {},
    render: {},
  };

  public canvas: HTMLCanvasElement;
  public state: PDFState | null;
  public scale: number = 1;

  constructor(
    private readonly lib: PDFLib,
    readonly id = uid(),
  ) {
    // create a background canvas for rendering the image at full scale,
    // but never append it to the dom.
    this.canvas = document.createElement("canvas");
    this.canvas.id = this.id;
    const context = this.canvas.getContext("2d");
    assertIsNotNull(context);
    this.context = context;
  }

  private setState(state: PDFState | null) {
    this.state = state;
    Object.values(this.subscribers["stateChange"]).forEach((s) => {
      assertIsNotNull(this.state);
      s(this.state);
    });
  }

  destroy() {
    this.setState(null);
  }

  on(e: PDFRendererEvent, handler: (state: PDFState) => void) {
    const id = uid();
    this.subscribers[e] = { [id]: handler };
    return id;
  }

  off(e: PDFRendererEvent, id: string) {
    delete this.subscribers[e][id];
  }

  getAABBData(aabb: AABB2) {
    const { minX, maxX, minY, maxY } = aabb;
    const sx = Math.round(minX * this.canvas.width);
    const sy = Math.round((1 - maxY) * this.canvas.height);
    const sw = Math.round((maxX - minX) * this.canvas.width);
    const sh = Math.round((maxY - minY) * this.canvas.height);

    // return full canvas on invalid aabb selecion
    if (sw <= 0 || sh <= 0) {
      return this.getData();
    }

    return this.context.getImageData(sx, sy, sw, sh);
  }

  getData() {
    return this.context.getImageData(0, 0, this.canvas.width, this.canvas.height);
  }

  getDataURL() {
    return this.canvas.toDataURL();
  }

  getPageCount() {
    return this.state?.pdf.numPages ?? 0;
  }

  getPageIndex() {
    return this.state?.pdfPageIndex ?? 0;
  }

  async setPageIndex(pdfPageIndex: number): Promise<PDFState> {
    return new Promise((res) => {
      assertIsNotNull(this.state);
      if (pdfPageIndex > 0 && pdfPageIndex < this.getPageCount()) {
        this.state.pdf.getPage(pdfPageIndex).then((pdfPage: any) => {
          assertIsNotNull(this.state);
          this.setState({
            ...this.state,
            pdfPage,
            pdfPageIndex,
          });

          res(this.state);
        });
      } else {
        res(this.state);
      }
    });
  }

  async loadPDFURL(url: string, pdfPageIndex: number = 1): Promise<PDFState> {
    return new Promise((res) => {
      this.lib.getDocument(url).then((pdf: any) => {
        pdf.getPage(pdfPageIndex).then((pdfPage: any) => {
          this.setState({
            pdf,
            pdfPage,
            pdfPageIndex,
            pdfImg: null,
          });

          assertIsNotNull(this.state);
          res(this.state);
        });
      });
    });
  }

  async loadPDF(pdfFile: File, pdfPageIndex: number = 1): Promise<PDFState> {
    return new Promise((res) => {
      loadFile(pdfFile, (data) => {
        this.lib.getDocument({ data }).then((pdf: any) => {
          pdf.getPage(pdfPageIndex).then((pdfPage: any) => {
            this.setState({
              pdf,
              pdfPage,
              pdfPageIndex,
              pdfImg: null,
            });

            assertIsNotNull(this.state);
            res(this.state);
          });
        });
      });
    });
  }

  async render(): Promise<string | undefined> {
    if (!this.state) {
      return undefined;
    }

    const { pdfPage, pdfPageIndex } = this.state;

    const t0 = performance.now();

    const viewport = pdfPage.getViewport({ scale: this.scale });

    this.canvas.height = viewport.height;
    this.canvas.width = viewport.width;

    await pdfPage.render({
      canvasContext: this.context,
      viewport,
      transform: null,
    });

    const t1 = performance.now();
    console.warn(`rendered page ${pdfPageIndex} in ${t1 - t0}`);
    return undefined;
  }

  async renderAABBs(aabbs: AABB2[], color = "#000000") {
    const { width, height } = this.canvas;

    this.context.strokeStyle = color;
    this.context.lineWidth = 2;

    for (let i = 0, l = aabbs.length; i < l; i++) {
      const { minX, minY, maxX, maxY } = aabbs[i];
      const y = (1 - maxY) * height;
      const x = minX * width;
      const w = (maxX - minX) * width;
      const h = (maxY - minY) * height;

      this.context.strokeRect(x, y, w, h);
    }
  }
}

//https://github.com/mozilla/pdf.js

const PDFJS = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.1.266/pdf.min.js";
const PDFID = "pdfjs";

// just some type safety here
interface PDFLib {
  getDocument: (pdf: any) => any;
}

function loadPDFLib(onload: (lib: PDFLib) => void) {
  const ctx = window as any;
  const existingScript = document.getElementById(PDFID);
  if (!existingScript) {
    const script = document.createElement("script");
    script.src = PDFJS;
    script.id = PDFID;
    document.body.appendChild(script);
    script.onload = () => {
      if (onload) {
        onload(ctx.pdfjsLib);
      }
    };
  }
  if (existingScript && onload) {
    onload(ctx.pdfjsLib);
  }
}

function loadFile(file: File, onload: (buffer: string | ArrayBuffer | null) => void) {
  const fileReader = new FileReader();
  fileReader.onload = (e) => {
    assertIsNotNull(e.target);
    onload(e.target.result);
  };
  fileReader.readAsBinaryString(file);
}

// function blobToFile(theBlob: Blob, fileName: string): File {
//   const b: any = theBlob;
//   //A Blob() is almost a File() - it's just missing the two properties below which we will add
//   b.lastModifiedDate = new Date();
//   b.name = fileName;

//   //Cast to a File() type
//   return theBlob as File;
// }

function remapValue(v: number, min: number, max: number, nMin: number, nMax: number) {
  return nMin + (v - min) * ((nMax - nMin) / (max - min));
}

export const Route = createFileRoute("/debug/pdf-diff")({
  component: PDFDiff,
});
